import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { publicApi, Demonstration, getMediaUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import MediaDialog, { ShortcutHint } from '../components/MediaDialog';
import FeedbackThread from '../components/FeedbackThread';
import VideoThumbnail from '../components/VideoThumbnail';
import './CoachPage.css';

type QueueItem = Demonstration & { awaitingReply: boolean };

function waiting(iso: string): string {
  const h = (Date.now() - new Date(iso).getTime()) / 3600_000;
  if (h < 1) return 'just now';
  if (h < 24) return `${Math.floor(h)}h waiting`;
  return `${Math.floor(h / 24)}d waiting`;
}

const titleOf = (d: Demonstration) =>
  d.demonstrationType === 'PATTERN' ? d.pattern?.title ?? 'Pattern' : d.move?.title ?? 'Move';

/** Instructor review queue: students who asked for feedback, longest-waiting first. */
const CoachPage: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<QueueItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<QueueItem | null>(null);
  const [filter, setFilter] = useState<'awaiting' | 'all'>('awaiting');

  const isStaff = (user?.role === 'owner' || user?.role === 'instructor') && user?.instructorId != null;

  useEffect(() => {
    if (!isStaff) return;
    publicApi.demonstrations
      .getQueue()
      .then(setItems)
      .catch(() => setError('Couldn’t load the review queue.'));
  }, [isStaff]);

  const markReplied = useCallback((id: string) => {
    setItems((prev) => prev?.map((d) => (String(d.id) === String(id) ? { ...d, awaitingReply: false } : d)) ?? null);
    setSelected((s) => (s && String(s.id) === String(id) ? { ...s, awaitingReply: false } : s));
  }, []);

  if (user && !isStaff) {
    return (
      <div className="coach-page">
        <h1>Review queue</h1>
        <p className="coach-sub">The review queue is for instructors.</p>
      </div>
    );
  }

  const awaitingCount = items?.filter((d) => d.awaitingReply).length ?? 0;
  const shown = items?.filter((d) => filter === 'all' || d.awaitingReply) ?? [];

  return (
    <div className="coach-page">
      <header className="coach-header">
        <div>
          <h1>Review queue</h1>
          <p className="coach-sub">
            {items === null
              ? 'Loading…'
              : awaitingCount === 0
                ? 'You’re all caught up.'
                : `${awaitingCount} student${awaitingCount === 1 ? ' is' : 's are'} waiting on your feedback.`}
          </p>
        </div>
        <div className="coach-tabs" role="tablist" aria-label="Filter">
          <button
            type="button"
            role="tab"
            aria-selected={filter === 'awaiting'}
            className={`coach-tab ${filter === 'awaiting' ? 'is-on' : ''}`}
            onClick={() => setFilter('awaiting')}
          >
            Awaiting reply {awaitingCount > 0 && <span className="coach-tab-count">{awaitingCount}</span>}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filter === 'all'}
            className={`coach-tab ${filter === 'all' ? 'is-on' : ''}`}
            onClick={() => setFilter('all')}
          >
            All requests
          </button>
        </div>
      </header>

      {error && <div className="error">{error}</div>}

      {items !== null && shown.length === 0 && !error && (
        <div className="no-results">
          {filter === 'awaiting'
            ? <>Nothing waiting. <button type="button" className="btn btn-ghost btn-small" onClick={() => setFilter('all')}>See answered requests</button></>
            : <>No feedback requests yet. Students ask for feedback when they upload a demonstration.</>}
        </div>
      )}

      <div className="coach-grid">
        {shown.map((d) => (
          <article key={d.id} className={`coach-card ${d.awaitingReply ? 'is-awaiting' : ''}`}>
            <VideoThumbnail
              videoUrl={d.videoUrl}
              thumbnailUrl={d.thumbnail}
              alt={`${d.dancer?.name ?? 'Student'} — ${titleOf(d)}`}
              onClick={() => setSelected(d)}
            />
            <div className="coach-card-body">
              <div className="coach-card-top">
                <span className="coach-card-dancer">{d.dancer?.name}</span>
                <span className={`coach-status ${d.awaitingReply ? 'is-awaiting' : 'is-done'}`}>
                  {d.awaitingReply ? waiting(d.createdAt) : 'Replied'}
                </span>
              </div>
              <h3 className="coach-card-title">
                {d.demonstrationType === 'PATTERN' && d.pattern ? (
                  <Link to={`/library/patterns/${d.pattern.slug || d.pattern.id}`}>{titleOf(d)}</Link>
                ) : d.move ? (
                  <Link to={`/library/moves/${d.move.id}`}>{titleOf(d)}</Link>
                ) : titleOf(d)}
              </h3>
              {d.caption && <p className="coach-card-caption">“{d.caption}”</p>}
              <button type="button" className={`btn ${d.awaitingReply ? 'btn-primary' : 'btn-secondary'} btn-small coach-card-cta`} onClick={() => setSelected(d)}>
                {d.awaitingReply ? 'Review & reply' : 'Open thread'}
              </button>
            </div>
          </article>
        ))}
      </div>

      {selected && (
        <MediaDialog
          src={getMediaUrl(selected.videoUrl)}
          poster={selected.thumbnail ? getMediaUrl(selected.thumbnail) : undefined}
          title={`${selected.dancer?.name ?? 'Student'} — ${titleOf(selected)}`}
          onClose={() => setSelected(null)}
        >
          <div className="media-dialog-person">
            <span className="media-dialog-avatar" aria-hidden="true">{selected.dancer?.name?.charAt(0) || '?'}</span>
            <div>
              <strong>{selected.dancer?.name}</strong>
              <small>{titleOf(selected)} · {waiting(selected.createdAt)}</small>
            </div>
          </div>
          {selected.caption && <p className="media-dialog-quote">{selected.caption}</p>}
          <FeedbackThread
            demonstration={selected}
            onPosted={(_fb, asInstructor) => asInstructor && markReplied(String(selected.id))}
          />
          <ShortcutHint />
        </MediaDialog>
      )}
    </div>
  );
};

export default CoachPage;
